import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Target, Lightbulb, Wrench, ListChecks, ArrowRight, MessageCircle, Youtube } from "lucide-react";
import { PORTFOLIO } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const KIND_STYLES = {
  "Real-world experience": "border-emerald-500/50 bg-emerald-500/15 text-emerald-300",
  "Real workplace solution": "border-emerald-500/50 bg-emerald-500/15 text-emerald-300",
  "Personal software project": "border-sky-500/50 bg-sky-500/15 text-sky-300",
  "Content creation initiative": "border-rose-500/50 bg-rose-500/15 text-rose-300",
  "Demonstration of approach": "border-slate-400/40 bg-slate-400/10 text-slate-300",
  "Skills showcase": "border-slate-400/40 bg-slate-400/10 text-slate-300",
};

const KindChip = ({ kind, testid }) => (
  <span
    data-testid={testid}
    className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] backdrop-blur-sm ${KIND_STYLES[kind] || KIND_STYLES["Skills showcase"]}`}
  >
    {kind}
  </span>
);

export default function Portfolio() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  return (
    <section id="work" className="py-20 md:py-28" data-testid="portfolio-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          overline="Experience & portfolio"
          title="Genuine experience, honestly presented"
          copy="A mix of real workplace experience, solutions I have actually built, and demonstrations of my approach — each clearly labelled. No invented clients, no unverified figures: any benefits described are potential rather than measured, and genuine case studies will be added as the business grows."
        />

        <div className="mt-12 grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3">
          {PORTFOLIO.map((p, i) => (
            <Reveal key={p.title} delay={Math.min(i * 0.07, 0.35)}>
              <article
                data-testid={`portfolio-card-${i}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] transition-all duration-300 hover:-translate-y-1 hover:border-cyan-500/30 hover:shadow-[0_0_36px_rgba(0,240,255,0.12)]"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070B19] via-[#070B19]/25 to-transparent" />
                  <div className="absolute left-4 top-4">
                    <KindChip kind={p.kind} testid={`portfolio-kind-${i}`} />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-400">{p.category}</p>
                  <h3 className="font-display mt-2 text-base font-semibold leading-snug text-slate-50">{p.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-400">{p.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span key={t} className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-medium text-slate-400">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <button
                      data-testid={`view-project-button-${i}`}
                      onClick={() => setActive(p)}
                      className="inline-flex w-fit items-center gap-2 rounded-full border border-cyan-500/40 px-5 py-2.5 text-xs font-semibold text-cyan-300 transition-all duration-200 hover:bg-cyan-500/10 hover:shadow-[0_0_20px_rgba(0,240,255,0.2)]"
                    >
                      View Project
                      <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                    </button>
                    {p.link && (
                      <a
                        href={p.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`portfolio-youtube-link-${i}`}
                        aria-label={`${p.link.label} (opens in a new tab)`}
                        className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 px-4 py-2.5 text-xs font-semibold text-rose-300 transition-all duration-200 hover:bg-rose-500/10"
                      >
                        <Youtube size={15} />
                        YouTube
                      </a>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div
            className="relative mt-14 overflow-hidden rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-cyan-500/[0.08] via-[#0D152D] to-blue-600/[0.08] p-8 md:p-12"
            data-testid="cta-invite-banner"
          >
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[radial-gradient(closest-side,rgba(0,240,255,0.12),transparent)]" />
            <div className="relative max-w-3xl">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
                <MessageCircle size={14} />
                Work with me
              </p>
              <h3 className="font-display mt-4 text-xl font-bold text-slate-50 sm:text-2xl">
                Have a technical challenge or a process you would like to improve?
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
                Let's explore a practical solution together. With my Certificate of Higher Education in Computing and IT,
                Level 3 Excel skills and hands-on experience working with data and operational systems, I can help you
                analyse the problem, understand how the process works and identify an appropriate digital solution.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Get in touch to discuss your requirements and find out how WA Digital Solutions could help.
              </p>
              <button
                data-testid="cta-invite-button"
                onClick={() => scrollToId("enquiry")}
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-7 py-3.5 text-sm font-semibold text-[#04101F] shadow-[0_0_28px_rgba(0,240,255,0.35)] transition-transform duration-200 hover:scale-[1.03]"
              >
                Get in touch
                <ArrowRight size={15} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#04060E]/85 p-4 backdrop-blur-md sm:p-8"
            onClick={() => setActive(null)}
            data-testid="project-modal-backdrop"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-cyan-500/25 bg-[#0D152D] shadow-[0_0_60px_rgba(0,102,255,0.25)]"
              onClick={(e) => e.stopPropagation()}
              data-testid="project-modal"
              role="dialog"
              aria-modal="true"
              aria-label={active.title}
            >
              <div className="relative h-52 sm:h-64">
                <img src={active.img} alt={active.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D152D] via-[#0D152D]/30 to-transparent" />
                <button
                  data-testid="project-modal-close"
                  onClick={() => setActive(null)}
                  aria-label="Close project details"
                  className="absolute right-4 top-4 rounded-full border border-white/20 bg-[#070B19]/80 p-2 text-slate-300 backdrop-blur-sm transition-colors hover:border-cyan-400/60 hover:text-cyan-300"
                >
                  <X size={18} />
                </button>
                <div className="absolute bottom-4 left-6 right-6">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <KindChip kind={active.kind} testid="project-modal-kind" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">{active.category}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold text-slate-50 sm:text-2xl" data-testid="project-modal-title">
                    {active.title}
                  </h3>
                  {active.subtitle && <p className="mt-1 text-xs text-slate-400">{active.subtitle}</p>}
                </div>
              </div>

              <div className="space-y-7 p-6 sm:p-8">
                <p className="text-sm leading-relaxed text-slate-300">{active.description}</p>

                <div>
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <Target size={13} className="text-cyan-400" /> The challenge
                  </p>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{active.problem}</p>
                </div>

                <div>
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <Lightbulb size={13} className="text-cyan-400" /> My approach
                  </p>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{active.solution}</p>
                </div>

                <div>
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <Wrench size={13} className="text-cyan-400" /> Skills & tools involved
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="project-modal-tools">
                    {active.tools.map((t) => (
                      <span
                        key={t}
                        className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-slate-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.05] p-5">
                  <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">
                    <ListChecks size={13} /> Potential benefits
                  </p>
                  <ul className="mt-3 space-y-2">
                    {active.benefits.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 border-t border-cyan-500/15 pt-3 text-[11px] leading-relaxed text-slate-500" data-testid="project-modal-disclaimer">
                    Benefits shown are potential rather than measured. No client names, figures, confidential workplace
                    information or proprietary code are published.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    data-testid="project-modal-enquire-button"
                    onClick={() => {
                      setActive(null);
                      scrollToId("enquiry");
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-6 py-3 text-sm font-semibold text-[#04101F] transition-transform duration-200 hover:scale-[1.03]"
                  >
                    Discuss a project like this
                    <ArrowRight size={15} />
                  </button>
                  {active.link && (
                    <a
                      href={active.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="project-modal-youtube-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#FF0000] px-6 py-3 text-sm font-semibold text-white shadow-[0_0_24px_rgba(255,0,0,0.3)] transition-transform duration-200 hover:scale-[1.03]"
                    >
                      <Youtube size={17} />
                      {active.link.label}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
